import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpParameterCodec,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { ErrorHandler, Inject, Injectable } from '@angular/core';
import { Auth } from '@freelancer/auth';
import {
  ErrorResponseData,
  HttpAdapter,
  ResponseData,
} from '@freelancer/datastore';
import * as Rx from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { FREELANCER_HTTP_CONFIG } from './freelancer-http.config';
import { FreelancerHttpConfig } from './freelancer-http.interface';

type RequestParamsObjectArrayValue = ReadonlyArray<
  string | number | boolean | RequestParamsObject | undefined
>;

interface RequestParamsObject {
  [k: string]:
    | string
    | number
    | boolean
    | RequestParamsObjectArrayValue
    | RequestParamsObject
    | undefined;
}

interface RawSuccessResponseData<T> {
  status: 'success';
  result: T;
  request_id?: string;
}

function isDefined<T>(x?: T): x is T {
  return x !== undefined;
}

/**
 * Request options that serves as an alias for request options for HttpClient
 * methods (because they don't export them) plus a couple .
 * These are limited to currently the commonly used options for a direct
 * HTTP request. If you're missing anything that HttpClient has, please check
 * the docs on HttpClient requests and see if they are shared between all CRUD
 * methods first, then you're free to add it in.
 */
export interface RequestOptions {
  isGaf?: boolean;
  serializeBody?: boolean;
  params?: RequestParamsObject | HttpParams;
  reportProgress?: boolean;
  headers?: HttpHeaders;
}

/*
 * This removes the special parameter encoding done by Angular for apparently
 * no reason, see https://github.com/angular/angular/issues/18261.
 * FIXME: remove that when Angular has fixed it.
 */
class CustomEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }

  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }

  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}

@Injectable()
export class FreelancerHttp implements HttpAdapter {
  // Add compact=true to strip out null values on the response.
  // Add new_errors=true for new error format
  private readonly requestParams = {
    compact: 'true',
    new_errors: 'true',
  };

  constructor(
    private http: HttpClient,
    private auth: Auth,
    private errorHandler: ErrorHandler,
    @Inject(FREELANCER_HTTP_CONFIG)
    private freelancerHttpConfig: FreelancerHttpConfig,
  ) {}

  /**
   * Utility function to merge the Auth headers with the request headers.
   * @param authHeaders HttpHeaders from Auth
   * @param requestHeaders HttpHeaders specified on the request options.
   */
  private mergeAuthHeaderWithRequestHeaders(
    authHeaders: HttpHeaders,
    requestHeaders?: HttpHeaders,
  ) {
    if (!requestHeaders) {
      return authHeaders;
    }

    return authHeaders.keys().reduce((obj, key) => {
      const values = authHeaders.getAll(key);
      return values ? requestHeaders.append(key, values) : requestHeaders;
    }, requestHeaders);
  }

  /**
   * Get the base URL to use, whether or not it's a GAF endpoint or a Freelancer
   * API endpoint.
   * @param config Configuration object.
   * @param config.isGaf True if GAF endpoint, false if API endpoint.
   */
  getBaseUrl(config: { isGaf?: boolean } = {}) {
    return config.isGaf
      ? this.freelancerHttpConfig.ajaxBaseUrl
      : this.freelancerHttpConfig.apiBaseUrl;
  }

  /**
   * Format response body to the interface that we want to return.
   * For now it's only adding a status code into the response body.
   * @param response The HttpResponse object.
   */
  private formatResponseBody<T>(
    response: HttpResponse<RawSuccessResponseData<T>>,
  ): ResponseData<T> {
    if (response.body === null) {
      console.error('No response body', response);
      return {
        status: 'error',
        errorCode: 'UNKNOWN_ERROR',
      };
    }

    if (response.body.status !== 'success') {
      console.error('Malformed backend response', response.body);
      return {
        status: 'error',
        errorCode: 'UNKNOWN_ERROR',
      };
    }

    return {
      status: response.body.status,
      result: response.body.result,
      requestId: response.body.request_id,
    };
  }

  private reportErrorResponse(error: HttpErrorResponse) {
    if (error.error instanceof Error) {
      // A client-side error occurred.
      this.errorHandler.handleError(error.error);
    } else {
      // We don't log backend errors in Sentry as there are already logged in
      // Kibana
      console.error(error.message, error.error);
    }
  }

  private formatErrorBody(error: HttpErrorResponse): ErrorResponseData {
    if (error.error.error !== undefined) {
      return {
        status: 'error',
        errorCode: error.error.error.code,
        requestId: error.error.request_id,
      };
    }

    // Another type of backend error occured, e.g. External LB or Varnish.
    return {
      status: 'error',
      errorCode: 'UNKNOWN_ERROR',
    };
  }

  /**
   * Helper function to convert an object to HttpParams, which is conformant
   * to the `application/x-www-form-urlencoded` MIME type.
   *
   * @param paramsObject The object to convert. Limited to string, number,
   * boolean primitives as values, array containing those primitives, or
   * nested objects. This means NO nested arrays, e.g. [1, [2, 3]]
   */
  _serialize(paramsObject: RequestParamsObject | HttpParams = {}) {
    if (paramsObject instanceof HttpParams) {
      return paramsObject;
    }

    return Object.entries(paramsObject).reduce<HttpParams>(
      (httpParams, [key, value]) => {
        if (Array.isArray(value)) {
          return (value as RequestParamsObjectArrayValue)
            .filter(isDefined)
            .reduce((innerParams, arrayItem) => {
              // FIXME: This is not allowed according to the type definition
              // of RequestParamsObject, so this branch is unlikely to be taken
              if (Array.isArray(arrayItem)) {
                // Make sure that batching is handled here. Break all & into more
                // params additions.
                return String(arrayItem as RequestParamsObjectArrayValue)
                  .split('&')
                  .reduce(
                    (innerInnerParams, arrayBatchItem) =>
                      innerInnerParams.append(
                        `${key}[]`,
                        typeof arrayBatchItem !== 'string'
                          ? JSON.stringify(arrayBatchItem)
                          : arrayBatchItem,
                      ),
                    innerParams,
                  );
              }

              // Stringify the array item because we need a string and if it's an
              // inner object just stringify to JSON.
              return innerParams.append(
                `${key}[]`,
                typeof arrayItem !== 'string'
                  ? JSON.stringify(arrayItem)
                  : arrayItem,
              );
            }, httpParams);
        }

        // Our REST API backend expects arrays foo=[1,2] to be encoded as
        // foo[]=1&foo[]=2 (but only for form-encoded payloads, see T74444)
        if (value !== undefined) {
          // Don't stringify the value if it's a string, otherwise you'll end up
          // with quotation marks surrounding the string (`"foo"` instead of `foo`).
          return httpParams.append(
            key,
            typeof value !== 'string' ? JSON.stringify(value) : value,
          );
        }

        return httpParams;
      },
      new HttpParams({ encoder: new CustomEncoder() }),
    );
  }

  /**
   * Construct a GET request to the backend.
   * @param endpoint The endpoint
   * @param options Request options
   */
  get<T = any>(
    endpoint: string,
    options?: RequestOptions & { reportProgress?: false },
  ): Rx.Observable<ResponseData<T>>;
  get<T = any>(
    endpoint: string,
    options?: RequestOptions & { reportProgress: true },
  ): Rx.Observable<HttpEvent<ResponseData<T>>>;
  get<T = any>(endpoint: string, options: RequestOptions = {}) {
    const baseUrl = this.getBaseUrl({ isGaf: options.isGaf });

    // Add webapp=1 for tracking in the backend to API requests
    const requestParams = options.isGaf
      ? { ...options.params, ...this.requestParams }
      : { ...options.params, webapp: 1, ...this.requestParams };

    if (options.reportProgress) {
      return Rx.of(endpoint).pipe(
        withLatestFrom(this.auth.getAuthorizationHeader()),
        switchMap(([requestEndpoint, authHeader]) =>
          this.http.get<RawSuccessResponseData<T>>(
            `${baseUrl}/${requestEndpoint}`,
            {
              reportProgress: options.reportProgress,
              observe: 'events',
              params: this._serialize(requestParams),
              headers: this.mergeAuthHeaderWithRequestHeaders(
                authHeader,
                options.headers,
              ),
            },
          ),
        ),
        map(event => {
          if (event instanceof HttpResponse) {
            return event.clone({
              body: this.formatResponseBody<T>(event),
            });
          }

          return event;
        }),
        catchError((error: HttpErrorResponse) => {
          this.reportErrorResponse(error);
          return Rx.of(
            new HttpResponse<ErrorResponseData>({
              body: this.formatErrorBody(error),
            }),
          );
        }),
      );
    }

    return Rx.of(endpoint).pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([requestEndpoint, authHeader]) =>
        this.http.get<RawSuccessResponseData<T>>(
          `${baseUrl}/${requestEndpoint}`,
          {
            reportProgress: options.reportProgress,
            observe: 'response',
            params: this._serialize(requestParams),
            headers: this.mergeAuthHeaderWithRequestHeaders(
              authHeader,
              options.headers,
            ),
          },
        ),
      ),
      map(this.formatResponseBody),
      catchError((error: HttpErrorResponse) => {
        this.reportErrorResponse(error);
        return Rx.of(this.formatErrorBody(error));
      }),
    );
  }

  /**
   * Construct a POST request to the backend
   * @param endpoint The endpoint
   * @param body Request body
   * @param options Request options
   */
  post<T = any>(
    endpoint: string,
    body: any | null,
    options?: RequestOptions & { reportProgress?: false },
  ): Rx.Observable<ResponseData<T>>;
  post<T = any>(
    endpoint: string,
    body: any | null,
    options?: RequestOptions & { reportProgress: true },
  ): Rx.Observable<HttpEvent<ResponseData<T>>>;
  post<T = any>(
    endpoint: string,
    body: any | null,
    options: RequestOptions = {},
  ):
    | Rx.Observable<ResponseData<T>>
    | Rx.Observable<HttpEvent<ResponseData<T>>> {
    const baseUrl = this.getBaseUrl({ isGaf: options.isGaf });

    const requestBody = options.serializeBody ? this._serialize(body) : body;

    if (options.reportProgress) {
      return Rx.of(endpoint).pipe(
        withLatestFrom(this.auth.getAuthorizationHeader()),
        switchMap(([requestEndpoint, authHeader]) =>
          this.http.post<RawSuccessResponseData<T>>(
            `${baseUrl}/${requestEndpoint}`,
            requestBody,
            {
              reportProgress: options.reportProgress,
              observe: 'events',
              headers: this.mergeAuthHeaderWithRequestHeaders(
                authHeader,
                options.headers,
              ),
              params: this.requestParams,
            },
          ),
        ),
        map(event => {
          if (event instanceof HttpResponse) {
            return event.clone({
              body: this.formatResponseBody<T>(event),
            });
          }

          return event;
        }),
        catchError((error: HttpErrorResponse) => {
          this.reportErrorResponse(error);
          return Rx.of(
            new HttpResponse<ErrorResponseData>({
              body: this.formatErrorBody(error),
            }),
          );
        }),
      );
    }

    return Rx.of(endpoint).pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([requestEndpoint, authHeader]) =>
        this.http.post<RawSuccessResponseData<T>>(
          `${baseUrl}/${requestEndpoint}`,
          requestBody,
          {
            reportProgress: options.reportProgress,
            observe: 'response',
            headers: this.mergeAuthHeaderWithRequestHeaders(
              authHeader,
              options.headers,
            ),
            params: this.requestParams,
          },
        ),
      ),
      map(this.formatResponseBody),
      catchError((error: HttpErrorResponse) => {
        this.reportErrorResponse(error);
        return Rx.of(this.formatErrorBody(error));
      }),
    );
  }

  /**
   * Construct a PUT request to the backend
   * @param endpoint The endpoint
   * @param body Request body
   * @param options Request options
   */
  put<T = any>(
    endpoint: string,
    body: any | null,
    options?: RequestOptions & { reportProgress?: false },
  ): Rx.Observable<ResponseData<T>>;
  put<T = any>(
    endpoint: string,
    body: any | null,
    options?: RequestOptions & { reportProgress: true },
  ): Rx.Observable<HttpEvent<ResponseData<T>>>;
  put<T = any>(
    endpoint: string,
    body: any | null,
    options: RequestOptions = {},
  ):
    | Rx.Observable<ResponseData<T>>
    | Rx.Observable<HttpEvent<ResponseData<T>>> {
    const baseUrl = this.getBaseUrl({ isGaf: options.isGaf });

    const requestBody = options.serializeBody ? this._serialize(body) : body;

    if (options.reportProgress) {
      return Rx.of(endpoint).pipe(
        withLatestFrom(this.auth.getAuthorizationHeader()),
        switchMap(([requestEndpoint, authHeader]) =>
          this.http.put<RawSuccessResponseData<T>>(
            `${baseUrl}/${requestEndpoint}`,
            requestBody,
            // FIXME: <any> is needed because of
            // https://github.com/angular/angular/issues/23600
            {
              reportProgress: options.reportProgress,
              observe: 'events',
              headers: this.mergeAuthHeaderWithRequestHeaders(
                authHeader,
                options.headers,
              ),
              params: this.requestParams,
            } as any,
          ),
        ),
        map(event => {
          if (event instanceof HttpResponse) {
            return event.clone({
              body: this.formatResponseBody<T>(event),
            });
          }

          return event;
        }),
        catchError((error: HttpErrorResponse) => {
          this.reportErrorResponse(error);
          return Rx.of(
            new HttpResponse<ErrorResponseData>({
              body: this.formatErrorBody(error),
            }),
          );
        }),
      );
    }

    return Rx.of(endpoint).pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([requestEndpoint, authHeader]) =>
        this.http.put<RawSuccessResponseData<T>>(
          `${baseUrl}/${requestEndpoint}`,
          requestBody,
          {
            reportProgress: options.reportProgress,
            observe: 'response',
            headers: this.mergeAuthHeaderWithRequestHeaders(
              authHeader,
              options.headers,
            ),
            params: this.requestParams,
          },
        ),
      ),
      map(this.formatResponseBody),
      catchError((error: HttpErrorResponse) => {
        this.reportErrorResponse(error);
        return Rx.of(this.formatErrorBody(error));
      }),
    );
  }

  /**
   * Construct a DELETE request to the backend
   * @param endpoint The endpoint
   * @param options Request options
   */
  delete<T = any>(
    endpoint: string,
    options?: RequestOptions & { reportProgress?: false },
  ): Rx.Observable<ResponseData<T>>;
  delete<T = any>(
    endpoint: string,
    options?: RequestOptions & { reportProgress: true },
  ): Rx.Observable<HttpEvent<ResponseData<T>>>;
  delete<T = any>(
    endpoint: string,
    options: RequestOptions = {},
  ):
    | Rx.Observable<ResponseData<T>>
    | Rx.Observable<HttpEvent<ResponseData<T>>> {
    const baseUrl = this.getBaseUrl({ isGaf: options.isGaf });

    if (options.reportProgress) {
      return Rx.of(endpoint).pipe(
        withLatestFrom(this.auth.getAuthorizationHeader()),
        switchMap(([requestEndpoint, authHeader]) =>
          this.http.delete<RawSuccessResponseData<T>>(
            `${baseUrl}/${requestEndpoint}`,
            {
              reportProgress: options.reportProgress,
              observe: 'events',
              headers: this.mergeAuthHeaderWithRequestHeaders(
                authHeader,
                options.headers,
              ),
              params: this.requestParams,
            },
          ),
        ),
        map(event => {
          if (event instanceof HttpResponse) {
            return event.clone({
              body: this.formatResponseBody<T>(event),
            });
          }

          return event;
        }),
        catchError((error: HttpErrorResponse) => {
          this.reportErrorResponse(error);
          return Rx.of(
            new HttpResponse<ErrorResponseData>({
              body: this.formatErrorBody(error),
            }),
          );
        }),
      );
    }

    return Rx.of(endpoint).pipe(
      withLatestFrom(this.auth.getAuthorizationHeader()),
      switchMap(([requestEndpoint, authHeader]) =>
        this.http.delete<RawSuccessResponseData<T>>(
          `${baseUrl}/${requestEndpoint}`,
          {
            reportProgress: options.reportProgress,
            observe: 'response',
            headers: this.mergeAuthHeaderWithRequestHeaders(
              authHeader,
              options.headers,
            ),
            params: this.requestParams,
          },
        ),
      ),
      map(this.formatResponseBody),
      catchError((error: HttpErrorResponse) => {
        this.reportErrorResponse(error);
        return Rx.of(this.formatErrorBody(error));
      }),
    );
  }
}
